<template>
  <div class="item-image-update">
    <header class="page-header">
      <h1>Mise à jour des images produits</h1>
      <p>Sélectionnez un produit et ajoutez ou modifiez son image</p>
    </header>

    <section class="page-body">
      <div class="filters-section">
        <div class="filter-group">
          <label>Section</label>
          <select
            v-model="selectedSection"
            class="form-control"
            @change="filterItems"
          >
            <option value="">Toutes les sections</option>
            <option
              v-for="section in sections"
              :key="section.reference || section.id"
              :value="section.reference || section.id"
            >
              {{ section.fullName }}
            </option>
          </select>
        </div>
        <div class="filter-group">
          <div class="search-wrapper">
            <i class="bi bi-search"></i>
            <input
              v-model="searchQuery"
              type="text"
              class="form-control"
              placeholder="Rechercher un produit... (max 6 résultats)"
              @input="filterItems"
            />
          </div>
        </div>
      </div>

      <div v-if="loading" class="loading-state">
        <div class="loader"></div>
        <p>Chargement des produits...</p>
      </div>

      <div v-else-if="filteredItems.length === 0" class="empty-state">
        <i class="bi bi-box-seam"></i>
        <p>Aucun produit trouvé</p>
        <p class="empty-hint">
          Utilisez les filtres pour rechercher des produits (affichage de 6
          résultats maximum)
        </p>
      </div>

      <div v-else>
        <div class="items-grid">
          <div
            v-for="item in filteredItems"
            :key="item.reference || item.id"
            class="item-card"
            @click="selectItem(item)"
          >
            <div class="item-image-wrapper">
              <img
                v-if="item.image"
                :src="$imageUrl(item.image)"
                :alt="item.fullName"
                @error="handleImageError($event)"
              />
              <div v-else class="item-image-placeholder">
                <i class="bi bi-image"></i>
              </div>
              <div v-if="item.image" class="image-badge">
                <i class="bi bi-check-circle"></i>
              </div>
            </div>
            <div class="item-info">
              <h4>{{ item.fullName }}</h4>
              <p class="item-reference">{{ item.reference }}</p>
            </div>
          </div>
        </div>
        <p class="items-hint">
          Affichage de {{ filteredItems.length }} produit(s) (maximum 6
          résultats)
        </p>
      </div>
    </section>

    <!-- Image Upload Modal -->
    <div
      v-if="showImageModal && selectedItem"
      class="image-modal-overlay"
      @click="closeImageModal"
    >
      <div class="image-modal" @click.stop>
        <header class="modal-header">
          <div>
            <h3>{{ selectedItem.fullName }}</h3>
            <p>Réf. {{ selectedItem.reference }}</p>
          </div>
          <button class="btn btn-light icon-only" @click="closeImageModal">
            <i class="bi bi-x-lg"></i>
          </button>
        </header>

        <section class="modal-body">
          <div class="current-image-section">
            <label>Image actuelle</label>
            <div class="current-image-wrapper">
              <img
                v-if="selectedItem.image"
                :src="$imageUrl(selectedItem.image)"
                :alt="selectedItem.fullName"
                @error="handleImageError($event)"
              />
              <div v-else class="no-image">
                <i class="bi bi-image"></i>
                <p>Aucune image</p>
              </div>
            </div>
          </div>

          <div class="upload-section">
            <label>Nouvelle image</label>
            <div class="upload-options">
              <input
                ref="fileInput"
                type="file"
                accept="image/*"
                @change="handleFileSelect"
                style="display: none"
              />
              <input
                ref="cameraInput"
                type="file"
                accept="image/*"
                capture="environment"
                @change="handleFileSelect"
                style="display: none"
              />
              <button
                class="btn btn-primary btn-upload"
                @click="openFilePicker"
              >
                <i class="bi bi-folder2-open"></i>
                <span>Choisir depuis la galerie</span>
              </button>
              <button class="btn btn-success btn-upload" @click="openCamera">
                <i class="bi bi-camera"></i>
                <span>Prendre une photo</span>
              </button>
            </div>

            <div v-if="previewImage" class="preview-section">
              <label>Aperçu</label>
              <div class="preview-wrapper">
                <img :src="previewImage" alt="Preview" />
                <button
                  class="btn-remove-preview"
                  @click="removePreview"
                  title="Supprimer"
                >
                  <i class="bi bi-x-lg"></i>
                </button>
              </div>
            </div>
          </div>
        </section>

        <footer class="modal-footer">
          <button class="btn btn-light" @click="closeImageModal">
            Annuler
          </button>
          <button
            class="btn btn-primary"
            @click="uploadImage"
            :disabled="!previewImage || uploading"
          >
            <i class="bi bi-upload"></i>
            <span v-if="!uploading">Enregistrer</span>
            <span v-else>Enregistrement...</span>
          </button>
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "ItemImageUpdate",
  data() {
    return {
      loading: false,
      uploading: false,
      sections: [],
      items: [],
      selectedSection: "",
      searchQuery: "",
      showImageModal: false,
      selectedItem: null,
      previewImage: null,
      selectedFile: null,
    };
  },
  computed: {
    filteredItems() {
      let filtered = this.items;

      // Filter by section
      if (this.selectedSection) {
        filtered = filtered.filter(
          (item) =>
            item.section_reference === this.selectedSection ||
            item.section?.reference === this.selectedSection
        );
      }

      // Filter by search query
      if (this.searchQuery.trim()) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(
          (item) =>
            (item.fullName && item.fullName.toLowerCase().includes(query)) ||
            (item.reference && item.reference.toLowerCase().includes(query))
        );
      }

      // Limit to 6 items
      return filtered.slice(0, 6);
    },
  },
  methods: {
    async loadSections() {
      try {
        const response = await this.$api.instance.get("/api/sections");
        this.sections = Array.isArray(response.data) ? response.data : [];
      } catch (error) {
        console.error("Error loading sections", error);
        this.$toast?.error?.("Impossible de charger les sections.");
      }
    },
    async loadItems() {
      this.loading = true;
      try {
        const response = await this.$api.instance.get("/api/items");
        this.items = Array.isArray(response.data) ? response.data : [];
      } catch (error) {
        console.error("Error loading items", error);
        this.$toast?.error?.("Impossible de charger les produits.");
      } finally {
        this.loading = false;
      }
    },
    filterItems() {
      // Computed property handles filtering
    },
    selectItem(item) {
      this.selectedItem = item;
      this.previewImage = null;
      this.selectedFile = null;
      this.showImageModal = true;
    },
    closeImageModal() {
      if (this.uploading) return;
      this.showImageModal = false;
      this.selectedItem = null;
      this.previewImage = null;
      this.selectedFile = null;
    },
    openFilePicker() {
      this.$refs.fileInput.click();
    },
    openCamera() {
      this.$refs.cameraInput.click();
    },
    handleFileSelect(event) {
      const file = event.target.files[0];
      if (!file) return;

      // Validate file type
      if (!file.type.startsWith("image/")) {
        this.$toast?.error?.("Veuillez sélectionner une image.");
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        this.$toast?.error?.("L'image ne doit pas dépasser 5MB.");
        return;
      }

      this.selectedFile = file;

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        this.previewImage = e.target.result;
      };
      reader.readAsDataURL(file);

      // Reset input
      event.target.value = "";
    },
    removePreview() {
      this.previewImage = null;
      this.selectedFile = null;
    },
    async uploadImage() {
      if (!this.selectedFile || !this.selectedItem) return;

      this.uploading = true;
      try {
        // Fetch fresh item data to ensure we have all current values
        let currentItem = this.selectedItem;
        try {
          const itemResponse = await this.$api.instance.get(
            `/api/items/${this.selectedItem.reference}`
          );
          if (itemResponse.data && itemResponse.data.data) {
            currentItem = itemResponse.data.data;
          } else if (itemResponse.data) {
            currentItem = itemResponse.data;
          }
        } catch (fetchError) {
          console.warn(
            "Could not fetch fresh item data, using cached data",
            fetchError
          );
          // Continue with selectedItem if fetch fails
        }

        const data = currentItem;
        let formData = new FormData();
        formData.append("reference", data.reference || "");
        formData.append("image", this.selectedFile);
        formData.append("fullName", data.fullName || "");
        formData.append("code", data.code != null ? String(data.code) : "");
        formData.append(
          "description",
          data.description != null ? String(data.description) : ""
        );
        formData.append("tva", data.tva != null ? Number(data.tva) : 0);
        formData.append(
          "priceTTC",
          data.priceTTC != null ? Number(data.priceTTC) : 0
        );
        formData.append(
          "measuring",
          data.measuring != null ? String(data.measuring) : ""
        );
        formData.append("pcs", data.pcs != null ? Number(data.pcs) : 1);
        formData.append(
          "section_reference",
          data.section_reference != null ? String(data.section_reference) : ""
        );

        // Debug: Log what we're sending
        console.log("Sending FormData for item:", data.reference, {
          fullName: data.fullName,
          code: data.code,
          description: data.description,
          tva: data.tva,
          priceTTC: data.priceTTC,
          measuring: data.measuring,
          pcs: data.pcs,
          section_reference: data.section_reference,
        });

        const reference = data.reference;
        const response = await this.$api.instance.post(
          `/api/items/${reference}`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );

        if (response.data && response.data.status === "success") {
          this.$toast?.success?.("Image mise à jour avec succès.");

          // Get the updated item data from response
          const updatedItem = response.data.data || {};

          // Update the selectedItem to show new image in modal before closing
          if (this.selectedItem) {
            this.selectedItem = { ...this.selectedItem, ...updatedItem };
          }

          // Update the item in the items array to show new image in grid
          const index = this.items.findIndex(
            (i) =>
              (i.reference || i.id) ===
              (this.selectedItem.reference || this.selectedItem.id)
          );
          if (index !== -1) {
            // Use Vue.set or direct assignment to ensure reactivity
            this.$set(this.items, index, {
              ...this.items[index],
              ...updatedItem,
            });
          }

          // Close modal after a brief delay to show the updated image
          setTimeout(() => {
            this.closeImageModal();
          }, 500);

          // Reload items in background to ensure we have the latest data
          this.loadItems().catch((err) => {
            console.warn("Background reload failed:", err);
          });
        } else {
          throw new Error(
            response.data?.message || "Erreur lors de la mise à jour"
          );
        }
      } catch (error) {
        console.error("Error uploading image", error);
        const errorMessage =
          error.response?.data?.message ||
          "Impossible de mettre à jour l'image.";
        this.$toast?.error?.(errorMessage);
      } finally {
        this.uploading = false;
      }
    },
    handleImageError(event) {
      if (event && event.target) {
        event.target.style.display = "none";
      }
    },
  },
  mounted() {
    this.loadSections();
    this.loadItems();
  },
};
</script>

<style scoped>
.item-image-update {
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  padding: 2rem 2.5rem 4rem;
}

.page-header {
  margin-bottom: 2rem;
}

.page-header h1 {
  margin: 0 0 0.5rem;
  font-size: 2.25rem;
  font-weight: 700;
  color: #0f172a;
}

.page-header p {
  margin: 0;
  color: #64748b;
  font-size: 1rem;
}

.filters-section {
  display: grid;
  grid-template-columns: 250px 1fr;
  gap: 1rem;
  margin-bottom: 2rem;
  background: white;
  padding: 1.5rem;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.filter-group label {
  font-weight: 600;
  color: #475569;
  font-size: 0.9rem;
}

.search-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.search-wrapper i {
  position: absolute;
  left: 1rem;
  color: #94a3b8;
  font-size: 1.1rem;
}

.search-wrapper input {
  padding-left: 2.75rem;
}

.form-control {
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  padding: 0.75rem 1rem;
  font-size: 0.95rem;
  transition: all 0.2s ease;
  background: white;
}

.form-control:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 4rem 1rem;
  background: white;
  border-radius: 16px;
  color: #64748b;
}

.loader {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: 4px solid #e2e8f0;
  border-top-color: #3b82f6;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.empty-state i {
  font-size: 3rem;
  color: #cbd5e1;
}

.empty-hint {
  margin: 0.5rem 0 0;
  font-size: 0.85rem;
  color: #94a3b8;
  font-style: italic;
}

.items-hint {
  margin: 1rem 0 0;
  text-align: center;
  font-size: 0.9rem;
  color: #64748b;
  font-style: italic;
}

.items-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1.25rem;
}

.item-card {
  background: white;
  border-radius: 14px;
  padding: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.item-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
  border-color: #3b82f6;
}

.item-image-wrapper {
  position: relative;
  width: 100%;
  aspect-ratio: 1;
  border-radius: 12px;
  overflow: hidden;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
}

.item-image-wrapper img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.item-image-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: #cbd5e1;
  font-size: 2.5rem;
}

.image-badge {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #10b981;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9rem;
}

.item-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.item-info h4 {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 600;
  color: #0f172a;
  line-height: 1.3;
}

.item-reference {
  margin: 0;
  font-size: 0.8rem;
  color: #64748b;
}

.image-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  z-index: 2000;
  backdrop-filter: blur(4px);
}

.image-modal {
  width: min(600px, 100%);
  max-height: 90vh;
  background: white;
  border-radius: 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  padding: 1.75rem;
  border-bottom: 1px solid #e2e8f0;
}

.modal-header h3 {
  margin: 0 0 0.25rem;
  font-size: 1.4rem;
  font-weight: 700;
  color: #0f172a;
}

.modal-header p {
  margin: 0;
  color: #64748b;
  font-size: 0.9rem;
}

.modal-body {
  padding: 1.75rem;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.current-image-section,
.upload-section {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.current-image-section label,
.upload-section label {
  font-weight: 600;
  color: #475569;
  font-size: 0.95rem;
}

.current-image-wrapper {
  width: 100%;
  aspect-ratio: 1;
  max-width: 300px;
  border-radius: 12px;
  overflow: hidden;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #e2e8f0;
}

.current-image-wrapper img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.no-image {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  color: #cbd5e1;
  padding: 2rem;
}

.no-image i {
  font-size: 3rem;
}

.upload-options {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.btn-upload {
  flex: 1;
  min-width: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.875rem 1.25rem;
}

.preview-section {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.preview-wrapper {
  position: relative;
  width: 100%;
  aspect-ratio: 1;
  max-width: 300px;
  border-radius: 12px;
  overflow: hidden;
  border: 2px solid #3b82f6;
}

.preview-wrapper img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.btn-remove-preview {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: rgba(239, 68, 68, 0.9);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-remove-preview:hover {
  background: #ef4444;
  transform: scale(1.1);
}

.modal-footer {
  padding: 1.5rem 1.75rem;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  border-radius: 10px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.95rem;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-light {
  background: #f1f5f9;
  color: #475569;
}

.btn-light:hover:not(:disabled) {
  background: #e2e8f0;
}

.btn-primary {
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  color: white;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.btn-primary:hover:not(:disabled) {
  box-shadow: 0 6px 16px rgba(59, 130, 246, 0.4);
  transform: translateY(-1px);
}

.btn-success {
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.btn-success:hover:not(:disabled) {
  box-shadow: 0 6px 16px rgba(16, 185, 129, 0.4);
  transform: translateY(-1px);
}

.btn.icon-only {
  padding: 0.5rem;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
}

@media (max-width: 768px) {
  .item-image-update {
    padding: 1.5rem 1rem 4rem;
  }

  .filters-section {
    grid-template-columns: 1fr;
  }

  .items-grid {
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 1rem;
  }

  .image-modal {
    max-height: 95vh;
    border-radius: 20px 20px 0 0;
  }

  .image-modal-overlay {
    align-items: flex-end;
    padding: 0;
  }

  .upload-options {
    flex-direction: column;
  }

  .btn-upload {
    width: 100%;
  }
}
</style>
